import React from "react";
import { Link } from "react-router-dom";
import { PROJECTS } from "../data/projects";
import { Logo } from "../components/Logo";
import { ArrowRight, Sparkles } from "lucide-react";

// Explicit featured slugs (cyber-y2k, eos, twice-as-nice, nike-bratz)
const FEATURED_SLUGS = [
  "cyber-y2k-editorial",
  "eos-product-story",
  "twice-as-nice",
  "nike-x-bratz-fab-fit",
];
const featured = FEATURED_SLUGS
  .map((slug) => PROJECTS.find((p) => p.slug === slug))
  .filter(Boolean)
  .map((p) => ({ slug: p.slug, image: p.cover, title: p.title }));

export default function Home() {
  return (
    <section className="max-w-7xl mx-auto px-4 md:px-10 py-6 md:py-10" data-testid="home-page">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-stretch">
        {/* LEFT: Big primary logo */}
        <div className="lg:col-span-6 flex flex-col gap-5">
          <div className="flex-1 flex items-center justify-center min-h-[420px] md:min-h-[560px]">
            <Logo variant="primary" size="xl" className="drop-shadow-[6px_6px_0_rgba(0,0,0,0.15)]" />
          </div>

          {/* category chip bar — sits under the logo, like the reference orange pill */}
          <div className="bg-orange-400 border-4 border-black rounded-full px-5 py-3 shadow-[6px_6px_0_0_rgba(0,0,0,1)] flex flex-wrap items-center justify-center gap-3">
            {["BRANDING", "UX/UI", "PHOTOGRAPHY"].map((c) => (
              <span
                key={c}
                data-testid={`hero-chip-${c.toLowerCase().replace(/\W/g, "-")}`}
                className="font-body font-extrabold text-white text-xs md:text-sm uppercase tracking-widest"
              >
                {c}
              </span>
            ))}
          </div>

          <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
            <Link
              to="/projects"
              data-testid="cta-view-projects"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full border-4 border-black bg-pink-600 text-white font-body font-extrabold uppercase tracking-wider text-sm shadow-[5px_5px_0_0_rgba(0,0,0,1)] hover:translate-x-[3px] hover:translate-y-[3px] hover:shadow-[2px_2px_0_0_rgba(0,0,0,1)] transition-all"
            >
              View Projects <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              data-testid="cta-contact"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full border-4 border-black bg-yellow-300 text-black font-body font-extrabold uppercase tracking-wider text-sm shadow-[5px_5px_0_0_rgba(0,0,0,1)] hover:translate-x-[3px] hover:translate-y-[3px] hover:shadow-[2px_2px_0_0_rgba(0,0,0,1)] transition-all"
            >
              Let&apos;s Talk
            </Link>
          </div>
        </div>

        {/* RIGHT: Cream card with circular thumbnails */}
        <div className="lg:col-span-6">
          <div className="bg-[#FEFBEA] border-4 border-black rounded-[2rem] p-5 md:p-8 shadow-[10px_10px_0_0_rgba(0,0,0,1)] h-full">
            <div className="flex items-center justify-between mb-4 md:mb-6">
              <h2 className="font-display text-3xl md:text-4xl text-black tracking-wide">
                FEATURED <span className="text-pink-600">WORK</span>
              </h2>
              <Sparkles className="w-6 h-6 text-pink-600" />
            </div>

            <div className="grid grid-cols-2 gap-4 md:gap-6">
              {featured.map((p) => (
                <Link
                  to={`/projects/${p.slug}`}
                  key={p.slug}
                  data-testid={`featured-thumb-${p.slug}`}
                  className="group"
                >
                  <div className="relative aspect-square rounded-full border-4 border-black overflow-hidden cursor-pointer shadow-[5px_5px_0_0_rgba(0,0,0,1)] hover:translate-x-[3px] hover:translate-y-[3px] hover:shadow-[1px_1px_0_0_rgba(0,0,0,1)] transition-all bg-pink-200">
                    <img
                      src={p.image}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                  <p className="mt-3 text-center font-display text-lg md:text-2xl text-black uppercase">
                    {p.title}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
